import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiClock, FiHelpCircle, FiMessageCircle, FiSend, FiShield, FiUsers, FiX } from "react-icons/fi";
import "../styles/Home.css";

const HELP_TOPICS = [
  { question: "How do I register?", keywords: ["register", "registration", "account", "sign up", "registering steps"], answer: "Registration steps:\n1. Select Register on the Home page.\n2. Enter your correct personal and service details.\n3. Select your service, formation, unit, branch or sub-branch as shown.\n4. Select your official appointment, including admin appointments where authorized.\n5. Create and confirm your login password using the password guidance.\n6. Review the details and submit.\n7. Wait for the responsible administrator to approve the account before signing in.\n\nIf your unit, appointment or admin role is missing, call Admin Support instead of choosing an incorrect option." },
  { question: "How do I log in?", keywords: ["login", "log in", "sign in", "password", "credentials", "login steps"], answer: "Login steps:\n1. Select Get Started on the Home page.\n2. Enter your registered username and password.\n3. Select Sign In.\n4. If requested, enter the current code from your Authenticator app.\n5. Wavey will open the dashboard for your approved appointment or admin role.\n\nIf access is denied, confirm that your account has been approved. Use the recovery option if you forgot your password or lost Authenticator access. Never share a password or OTP code with anyone." },
  { question: "How do I create a password?", keywords: ["create password", "set password", "setting password", "new password", "admin password", "user password", "strong password", "password rules"], answer: "Password setup guidance for users and admins:\n1. Create your password only on the official Wavey registration, login recovery or admin-approved reset screen.\n2. Use a strong password that is hard to guess and different from other systems.\n3. Avoid service numbers, names, unit names, ranks, appointments, phone numbers or simple sequences.\n4. Do not share the password with colleagues, clerks, admins or support staff.\n5. Do not type your real password into this help assistant.\n6. If you forgot it, use the recovery option or call Admin Support for an approved reset.\n\nAdmins should never create or request a user's password directly. They should guide the user to the official setup or reset flow." },
  { question: "How do I upload and send a file?", keywords: ["send", "upload", "share", "file", "document", "uploading steps"], answer: "Upload and sending steps:\n1. Sign in and open your dashboard.\n2. Select the file upload area and choose the document.\n3. Select its correct classification.\n4. For Confidential or Secret files, create the required document password using the password guidance.\n5. Choose the destination formation or brigade, unit, branch and recipient appointment.\n6. Confirm the selected destination.\n7. Select Upload and wait for the success message naming the recipient.\n8. Open Sent Files to confirm the destination, delivery status and history.\n\nIf Wavey reports that no approved recipient holds the appointment, recheck the destination or call Admin Support." },
  { question: "Where are my sent files?", keywords: ["sent", "sent files", "delivery", "delivered", "track"], answer: "Open Sent Files on your dashboard. It shows documents you sent, their recipient, destination, current status, and delivery history." },
  { question: "Why is my account pending?", keywords: ["pending", "approve", "approval", "waiting"], answer: "Registration requires approval by the administrator responsible for your unit. You can sign in after the account has been approved." },
  { question: "Why does a classified file need a password?", keywords: ["classified", "secret", "confidential", "restricted", "file password"], answer: "Confidential and Secret documents require a document password for additional protection. Create it only when Wavey asks for it during upload, avoid obvious words or service details, and share it only through an approved secure channel. Do not type the real document password into this help assistant." },
  { question: "The recipient is not available", keywords: ["recipient", "not available", "not found", "destination", "appointment"], answer: "Wavey sends only to an approved user holding that appointment in the selected destination. Recheck the formation, unit and appointment, or ask the unit administrator to confirm the recipient's account." },
  { question: "What appears in my Inbox?", keywords: ["inbox", "received", "incoming", "current recipient"], answer: "Your Inbox shows documents currently assigned to your approved appointment and unit scope. Open a file to see who sent it, its origin, classification, status and action history." },
  { question: "How do I forward a file?", keywords: ["forward", "forwarding", "next recipient", "route"], answer: "Open the document, select Forward File, choose the correct destination and appointment, add any required comment or attachment, and confirm. The movement is added to the document's tracked history." },
  { question: "What do the file statuses mean?", keywords: ["status", "pending", "opened", "actioned", "archived", "returned"], answer: "Pending means the current recipient has not completed action. Opened confirms receipt. Actioned means an authorized action was recorded. Returned or Resubmission Required sends it back for correction. Archived means it was moved to the official archive." },
  { question: "How do I correct a returned file?", keywords: ["correct", "correction", "resubmit", "resubmission", "returned file"], answer: "Open the returned document, review the correction reason, select the corrected file, describe the changes, and resubmit it. Wavey retains the earlier history for accountability." },
  { question: "How do delivery receipts work?", keywords: ["receipt", "opened", "read", "delivery history"], answer: "Wavey records when a document is delivered and when the intended recipient first opens it. The sender can review that delivery state and the document's action history in Sent Files." },
  { question: "How do I use the archive?", keywords: ["archive", "archived", "archive index", "search archive"], answer: "Authorized users can archive completed documents. Use the Archive or Search view to find records by title, index, classification, unit, dates or other available metadata." },
  { question: "How do I reset Authenticator access?", keywords: ["authenticator", "2fa", "verification code", "reset access"], answer: "Use the Authenticator recovery option on the login page. An administrator must verify and approve the reset before new setup credentials are issued." },
];

const SENSITIVE_VALUE_PATTERN = /\bmy\s+(password|passcode|otp|token|pin|secret key|service number)\s+is\s+\S+|\b(password|passcode|otp|token|pin|secret key|service number)\s*[:=]\s*\S+/i;
const CLASSIFIED_CONTENT_PATTERN = /\b(classified contents?|operational details?|secret document)\b/i;

const findHelpAnswer = (question) => {
  const normalized = String(question || "").toLowerCase().trim();
  if (SENSITIVE_VALUE_PATTERN.test(question) || CLASSIFIED_CONTENT_PATTERN.test(question)) {
    return "For your security, do not share passwords, OTP codes, tokens, service numbers, classified content or operational details here. Remove that information and call Admin Support for assistance.";
  }
  let best = null;
  let bestScore = 0;
  HELP_TOPICS.forEach((topic) => {
    const score = topic.keywords.reduce((total, keyword) => total + (normalized.includes(keyword) ? keyword.length : 0), 0);
    if (score > bestScore) { best = topic; bestScore = score; }
  });
  return best?.answer || "I couldn't find a reliable answer for that issue. Please call Admin Support or contact your unit administrator. Do not include passwords, OTP codes, classified information or operational details in this chat.";
};

const Home = () => {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpInput, setHelpInput] = useState("");
  const [helpMessages, setHelpMessages] = useState([
    { from: "assistant", text: "Hello. I’m the Wavey Help Assistant. How can I help you use the system?" },
  ]);
  const homeStyle = {
    "--home-hero-logo": `url(${process.env.PUBLIC_URL}/army-logo.png)`,
    "--home-frame-image": `url(${process.env.PUBLIC_URL}/FILE.png)`,
  };
  const trustItems = [
    {
      icon: FiShield,
      title: "Secure",
      text: "Your documents remain protected throughout every stage.",
    },
    {
      icon: FiClock,
      title: "Reliable",
      text: "Access your documents anytime, anywhere when needed.",
    },
    {
      icon: FiUsers,
      title: "Efficient",
      text: "Streamlined document management for coordinated action.",
    },
    {
      icon: FiShield,
      title: "Trusted",
      text: "Built to support Kenya Army digital workflow operations.",
    },
  ];

  const askForHelp = (question) => {
    const cleanQuestion = String(question || "").trim();
    if (!cleanQuestion) return;
    setHelpMessages((messages) => [...messages,
      { from: "user", text: cleanQuestion },
      { from: "assistant", text: findHelpAnswer(cleanQuestion) },
    ]);
    setHelpInput("");
  };

  return (
    <div className="home-shell" style={homeStyle}>
      <span className="home-corner home-corner-tl" aria-hidden="true" />
      <span className="home-corner home-corner-tr" aria-hidden="true" />
      <span className="home-corner home-corner-bl" aria-hidden="true" />
      <span className="home-corner home-corner-br" aria-hidden="true" />

      <section className="home-panel">
        <section className="home-hero">
          <div className="home-hero-overlay">
            <div className="home-hero-content">
              <span className="home-eyebrow">
                <span className="home-eyebrow-dot" />
                HQ Kenya Army - Digital Workflow
              </span>

              <h1 className="home-title">
                Kenya Army{" "}
                <span className="home-title-accent">Wavey</span>
              </h1>

              <h2 className="home-subtitle">Document Management System</h2>

              <div className="home-hero-actions">
                <button
                  className="home-btn-primary"
                  onClick={() => navigate("/login")}
                >
                  Get Started <FiArrowRight size={16} />
                </button>
                <button
                  className="home-btn-secondary"
                  onClick={() => navigate("/register")}
                >
                  Register <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="home-trust-strip">
          <div className="home-trust-header">
            <span className="home-trust-kicker">Operational Standards</span>
            <h3>Built for secure and dependable document workflow</h3>
          </div>

          <div className="home-trust-grid">
            {trustItems.map((item) => {
              const Icon = item.icon;

              return (
                <article key={item.title} className="home-trust-card">
                  <div className="home-trust-icon">
                    <Icon size={24} />
                  </div>
                  <div className="home-trust-copy">
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </section>

      <footer className="home-footer">
        <span>&copy; {new Date().getFullYear()} WAVEY - HQ Kenya Army</span>
        <span>Digital Workflow & Document Management Platform</span>
      </footer>

      {helpOpen && (
        <aside className="home-help-panel" aria-label="Wavey Help Assistant">
          <header className="home-help-header">
            <div className="home-help-avatar"><FiMessageCircle size={20} /></div>
            <div><strong>Wavey AI Help</strong><span>Private, system-aware assistance</span></div>
            <button type="button" onClick={() => setHelpOpen(false)} aria-label="Close help"><FiX size={20} /></button>
          </header>
          <div className="home-help-messages" aria-live="polite">
            {helpMessages.map((message, index) => (
              <div className={`home-help-message home-help-message-${message.from}`} key={`${message.from}-${index}`}>{message.text}</div>
            ))}
          </div>
          <div className="home-help-suggestions" aria-label="Frequently asked questions">
            {HELP_TOPICS.slice(0, 5).map((topic) => <button type="button" key={topic.question} onClick={() => askForHelp(topic.question)}>{topic.question}</button>)}
          </div>
          <form className="home-help-form" onSubmit={(event) => { event.preventDefault(); askForHelp(helpInput); }}>
            <input value={helpInput} onChange={(event) => setHelpInput(event.target.value)} placeholder="Type your question..." aria-label="Ask Wavey Help" />
            <button type="submit" disabled={!helpInput.trim()} aria-label="Send question"><FiSend size={18} /></button>
          </form>
          <small>Never share passwords, OTP codes or classified information. For unresolved issues, call Admin Support.</small>
        </aside>
      )}
      <button className="home-help-trigger" type="button" onClick={() => setHelpOpen((open) => !open)} aria-expanded={helpOpen} aria-label={helpOpen ? "Close Wavey Help" : "Open Wavey Help"}>
        {helpOpen ? <FiX size={22} /> : <FiHelpCircle size={22} />}<span>{helpOpen ? "Close" : "Need help?"}</span>
      </button>
    </div>
  );
};

export default Home;
